// @flow

import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { I18n, Translate } from 'react-redux-i18n';
import {
  applyFilters,
  fetchShoppingCenters,
  saveFilteredStoreRequest,
} from '../../state/shopping-centers/shoppingCentersService';
import {
  filterAssetFeatures,
  filterGLA,
  filterStore,
  filterVisitors,
  initialAppliedFilters,
  shoppingCenterGLAFilters,
  shoppingCenterVisitorFilters,
} from '../../state/shopping-centers/shoppingCenterFilter';
import { MALL_INFO } from '../../path/mallPath';
import type { Filter, ShoppingCenter } from '../../types/shoppingCenterType';
import type { History } from '../../types/commonTypes';

type Props = {
  assetFeatureList: string[],
  storeList: string[],
  shoppingCenterList: ShoppingCenter[],
  filteredShoppingCenterRequest: string,
  filteredSecondShoppingCenterRequest: string,
  shoppingCenterAppliedFilters: Filter[],
  applyFilters: Function,
  saveFilteredStoreRequest: Function,
  history: History
}

type State = {
  selectedFilter: string,
}

class ShoppingCenterFilters extends React.Component<Props, State> {
  state: State = {
    selectedFilter: '',
  };

  addSelectedFilter = (filter) => {
    const currentFilter = this.getCurrentSelectedFilter();
    if (currentFilter.selected.includes(filter)) {
      currentFilter.selected =
        currentFilter.selected.filter(elt => elt !== filter);
    } else {
      currentFilter.selected.push(filter);
    }

    this.props.applyFilters(
      this.props.shoppingCenterAppliedFilters,
      this.props.filteredShoppingCenterRequest,
    );
  };

  clearFilter = () => {
    this.props.shoppingCenterAppliedFilters.forEach((filter) => {
      filter.selected = [];
    });
    this.props.applyFilters(initialAppliedFilters, '');
    this.setState({
      selectedFilter: '',
    });
  };

  getCurrentFilterList = () => {
    switch (this.state.selectedFilter) {
      case filterStore:
        return this.props.storeList.filter(store => this.props.filteredSecondShoppingCenterRequest !== '' &&
          store
            .toLowerCase()
            .startsWith(this.props.filteredSecondShoppingCenterRequest.toLowerCase()));
      case filterGLA:
        return shoppingCenterGLAFilters;
      case filterVisitors:
        return shoppingCenterVisitorFilters;
      case filterAssetFeatures:
        return this.props.assetFeatureList;
      default:
        return [];
    }
  };

  getCurrentSelectedFilter = (): Filter => {
    const arr = this.props.shoppingCenterAppliedFilters
      .filter(elt => elt.label === this.state.selectedFilter);
    return arr[0];
  };

  updateSearchValue = (value) => {
    this.props.applyFilters(this.props.shoppingCenterAppliedFilters, value);
  };

  mustDisplayThirdColumn = () => {
    return (this.props.filteredShoppingCenterRequest !== ''
      && !!this.props.filteredShoppingCenterRequest)
      || this.state.selectedFilter !== '';
  };

  render() {
    return (
      <div className="filters">
        <div
          role="presentation"
          onClick={e => e.stopPropagation()}
          className="filter-first-step"
        >
          <div
            className={`search-input ${this.props.filteredShoppingCenterRequest === '' ? 'empty' : ''}`}
          >
            <input
              onChange={(e: EventHandler) => {
                this.updateSearchValue(e.target.value);
              }}
              placeholder="&#xF002;"
              value={this.props.filteredShoppingCenterRequest}
            />
          </div>
          {
            this.props.shoppingCenterAppliedFilters.map(filter => (
              <button
                key={filter.label}
                onClick={() => this.setState({ selectedFilter: filter.label })}
                className={`search-filter ${this.state.selectedFilter === filter.label ? 'active' : ''}`}
              >
                <div className="filter-title">{I18n.t(filter.label)}</div>
                {
                  filter.selected.length > 0 && (
                    <div className="filter-title">
                      {`${
                        filter.selected.length > 1 ?
                          `${filter.selected.length} ${I18n.t('filters.selected')}` :
                          filter.selected[0]
                      }`}
                    </div>
                  )
                }
              </button>
            ))
          }
          <button
            onClick={this.clearFilter}
            className="apollo-button"
          >
            <Translate value="filters.CLEAR_ALL_FILTER" />
          </button>
        </div>
        {
          this.getCurrentSelectedFilter()
          && (
            <div
              role="presentation"
              onClick={e => e.stopPropagation()}
              className="filter-second-step"
            >
              <div className="second-step-title">
                {I18n.t(this.state.selectedFilter)}&nbsp;<Translate value="filters.FILTER" />
                <button onClick={() => this.setState({ selectedFilter: '' })}>
                  <i className="fas fa-times-circle icon" />
                </button>
              </div>
              {
                this.state.selectedFilter === filterStore &&
                <div
                  className={`search-input ${this.props.filteredSecondShoppingCenterRequest === '' ? 'empty' : ''}`}
                >
                  {I18n.t('filters.findByStoreName')}
                  <input
                    value={this.props.filteredSecondShoppingCenterRequest}
                    onChange={e =>
                      this.props.saveFilteredStoreRequest(e.target.value)
                    }
                    placeholder="&#xF002;"
                  />
                </div>
              }
              <div className="filter-container">
                {
                  this.getCurrentFilterList()
                    .map(filter => (
                      <button
                        key={filter}
                        onClick={() => this.addSelectedFilter(filter)}
                        className="search-filter"
                      >
                        <div className="filter-title">{filter}</div>
                        {
                          this.getCurrentSelectedFilter().selected.includes(filter) &&
                          <i className="fas fa-check icon" />
                        }

                      </button>
                    ))
                }
              </div>
              <div>
                <button
                  onClick={this.clearFilter}
                  className="apollo-button"
                >
                  <Translate value="filters.CLEAR_FILTER" />
                </button>
              </div>
            </div>
          )
        }
        {
          this.mustDisplayThirdColumn()
          && (
            <div
              role="presentation"
              onClick={e => e.stopPropagation()}
              className="filter-third-step"
            >
              <div className="third-step-title">
                <Translate value="filters.Shopping_centers" />
              </div>
              <div className="filter-container">
                {
                  this.props.shoppingCenterList
                    .map(shoppingCenter => (
                      <button
                        key={shoppingCenter.pcCode}
                        onClick={() => this.props.history.push(`${MALL_INFO}?mall=${shoppingCenter.id}`)}
                        className="search-filter"
                      >
                        <div className="filter-title">{shoppingCenter.name}</div>
                      </button>
                    ))
                }
              </div>
            </div>
          )
        }
      </div>
    );
  }
}

export default withRouter(connect(state => ({
  assetFeatureList: state.shoppingCenterFilters.assetFeatureList,
  storeList: state.shoppingCenterFilters.storeList,
  shoppingCenterList: state.shoppingCenterFilters.filteredShoppingCenterList,
  filteredShoppingCenterRequest: state.shoppingCenterFilters.filteredShoppingCenterRequest,
  filteredSecondShoppingCenterRequest:
  state.shoppingCenterFilters.filteredSecondShoppingCenterRequest,
  shoppingCenterAppliedFilters: state.shoppingCenterFilters.shoppingCenterAppliedFilters,
}), {
  fetchShoppingCenters,
  applyFilters,
  saveFilteredStoreRequest,
})(ShoppingCenterFilters));
